import React, { useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { Location } from "history";
import { ButtonVariant, Dialog } from "@merge-api/merge-javascript-shared";

interface Props {
  /** True while there are unsaved scope edits — navigating away should be confirmed first. */
  hasUnsavedChanges: boolean;
  /** One-shot bypass: when set true (by Cancel / successful Save), the next navigation is allowed
   *  through without prompting. The guard consumes (resets) it. */
  allowNavigationRef: React.MutableRefObject<boolean>;
}

/**
 * Guards in-app navigation (clicking a different category chip, the left nav, browser back, etc.)
 * while there are unsaved scope edits, so the batched edits aren't silently dropped. Follows the
 * app's ConditionPresetConfirmDiscardChangesDialog pattern: the RR v5 Prompt blocks the transition
 * and we show a styled confirmation instead of the native confirm().
 */
const ScopesDiscardChangesDialog = ({ hasUnsavedChanges, allowNavigationRef }: Props) => {
  // state
  const [nextLocation, setNextLocation] = useState<Location>();

  // hooks
  const history = useHistory();

  // event handlers
  const onNavigateAway = (location: Location) => {
    // Cancel / Save already handled the edits — let that one navigation through.
    if (allowNavigationRef.current) {
      allowNavigationRef.current = false;
      return true;
    }
    // Otherwise block, stash where the user was headed, and open the confirmation.
    setNextLocation(location);
    return false;
  };

  const onDiscard = () => {
    if (nextLocation) history.push(nextLocation);
    // Clear so the dialog closes even when the target is the current route (no unmount).
    setNextLocation(undefined);
  };

  const onContinueEditing = () => setNextLocation(undefined);

  // render
  return (
    <>
      <Prompt when={hasUnsavedChanges && !nextLocation} message={onNavigateAway} />
      <Dialog
        variant="sm"
        title="You have unsaved changes"
        open={!!nextLocation}
        onClose={onContinueEditing}
        primaryButtonText="Discard changes"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        onPrimaryButtonClick={onDiscard}
        secondaryButtonText="Continue editing"
        secondaryButtonVariant={ButtonVariant.TextBlack}
        onSecondaryButtonClick={onContinueEditing}
      >
        Your changes will be lost if you leave this page.
      </Dialog>
    </>
  );
};

export default ScopesDiscardChangesDialog;
